import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { ChevronRight } from 'lucide-react'
import type { HelpCategory } from '@/config/helpCategories'
import { getCategorySections } from '@/config/helpCategories'
import { resolveHelpIcon } from './helpIcons'

interface HelpCategoryCardProps {
  category: HelpCategory
  /** Open the category. When a sectionId is supplied, that section is expanded/scrolled to. */
  onOpen: (categoryId: string, sectionId?: string) => void
}

/**
 * A browse-view card for one help category: icon, title, blurb, and a short list of
 * the topics it contains. The whole card opens the category; individual topics open
 * the category scrolled to that section.
 */
export default function HelpCategoryCard({ category, onOpen }: HelpCategoryCardProps) {
  const Icon = resolveHelpIcon(category.icon)
  const sections = getCategorySections(category)

  return (
    <Card className="flex flex-col h-full transition-colors hover:border-primary/40">
      <CardHeader>
        <button
          type="button"
          onClick={() => onOpen(category.id)}
          className="group flex items-start gap-3 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 rounded-md"
          aria-label={`Open ${category.label} help`}
        >
          <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-primary/10 text-primary">
            <Icon className="h-5 w-5" aria-hidden="true" />
          </span>
          <span className="min-w-0">
            <CardTitle className="flex items-center gap-1 text-base">
              {category.label}
              <ChevronRight
                className="h-4 w-4 shrink-0 text-muted-foreground transition-transform group-hover:translate-x-0.5"
                aria-hidden="true"
              />
            </CardTitle>
            <CardDescription className="mt-1">{category.description}</CardDescription>
          </span>
        </button>
      </CardHeader>
      <CardContent className="mt-auto">
        <ul className="space-y-1" role="list">
          {sections.map((section) => (
            <li key={section.id}>
              <button
                type="button"
                onClick={() => onOpen(category.id, section.id)}
                className="w-full truncate rounded px-2 py-1 text-left text-sm text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                {section.label}
              </button>
            </li>
          ))}
        </ul>
      </CardContent>
    </Card>
  )
}
